import { useMemo } from 'react';
import type { PieDatum } from './model';

export interface DonutChartProps {
  data: PieDatum[];
  /** svg coordinate height; the ring scales to this */
  size?: number;
  /** ring width */
  thickness?: number;
  /** big number in the hole (e.g. a total) */
  centerLabel?: string;
  /** small caption under the center label */
  centerSub?: string;
  /** per-slice colors when a datum has no `color` (cycled) */
  palette?: string[];
  className?: string;
  ariaLabel?: string;
}

const DEFAULT_PALETTE = ['var(--c-blue)', 'var(--c-violet)', 'var(--c-teal)', 'var(--c-amber)', 'var(--c-pink)', 'var(--c-slate)'];

function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

/** Donut arc path from `a0`→`a1` degrees, between inner and outer radius. */
function arc(cx: number, cy: number, rOut: number, rIn: number, a0: number, a1: number): string {
  const [ox0, oy0] = polar(cx, cy, rOut, a0);
  const [ox1, oy1] = polar(cx, cy, rOut, a1);
  const [ix1, iy1] = polar(cx, cy, rIn, a1);
  const [ix0, iy0] = polar(cx, cy, rIn, a0);
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M${ox0} ${oy0}A${rOut} ${rOut} 0 ${large} 1 ${ox1} ${oy1}L${ix1} ${iy1}A${rIn} ${rIn} 0 ${large} 0 ${ix0} ${iy0}Z`;
}

/** A donut with a centered total and a labelled legend, for showing a distribution. */
export const DonutChart = ({
  data,
  size = 200,
  thickness = 28,
  centerLabel,
  centerSub,
  palette = DEFAULT_PALETTE,
  className,
  ariaLabel = 'donut chart',
}: DonutChartProps) => {
  const W = size * 2.15;
  const cx = size * 0.5;
  const cy = size * 0.5;
  const rOut = size * 0.42;
  const rIn = rOut - thickness;

  const slices = useMemo(() => {
    const total = data.reduce((s, d) => s + d.value, 0) || 1;
    let a = 0;
    return data.map((d, i) => {
      const sweep = (d.value / total) * 360;
      const a0 = a;
      const a1 = a + sweep;
      a = a1;
      return {
        d,
        i,
        color: d.color ?? palette[i % palette.length],
        path: arc(cx, cy, rOut, rIn, a0, Math.min(a1, 359.999)),
        pct: Math.round((d.value / total) * 100),
      };
    });
  }, [data, cx, cy, rOut, rIn, palette]);

  const legendX = size * 0.98;
  const rowH = 22;
  const legendTop = cy - (data.length * rowH) / 2 + rowH / 2;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${size}`} role="img" aria-label={ariaLabel} className={className}>
      {slices.map((s) => (
        <path key={s.i} d={s.path} fill={s.color}>
          <title>{`${s.d.label}: ${s.d.value} (${s.pct}%)`}</title>
        </path>
      ))}
      {centerLabel ? (
        <text x={cx} y={cy - (centerSub ? 2 : -5)} textAnchor="middle" fontSize="26" fontWeight="600" fill="var(--text)">
          {centerLabel}
        </text>
      ) : null}
      {centerSub ? (
        <text x={cx} y={cy + 18} textAnchor="middle" fontSize="12" fill="var(--text-3)">
          {centerSub}
        </text>
      ) : null}
      {slices.map((s) => {
        const y = legendTop + s.i * rowH;
        return (
          <g key={`l${s.i}`}>
            <rect x={legendX} y={y - 9} width={12} height={12} rx={3} fill={s.color} />
            <text x={legendX + 20} y={y + 1} fontSize="13" fill="var(--text-2)">
              {s.d.label}
            </text>
            <text x={W - 6} y={y + 1} textAnchor="end" fontSize="13" fill="var(--text-3)">
              {s.d.value} ({s.pct}%)
            </text>
          </g>
        );
      })}
    </svg>
  );
};
